/**
 * ocrInvoiceService — Single entry point for invoice processing.
 *
 * ImportFacturaWizard ONLY calls processInvoice(file) from this service.
 * The wizard never knows which AI provider is running behind the scenes.
 *
 * Architecture:
 *   File (PDF/image)
 *   → processInvoice(file)
 *   → Edge Function: procesar-factura-compra
 *   → AI Provider (OpenAI by default, Gemini as alternative)
 *   → Structured JSON (same format ImportFacturaWizard already consumes)
 *
 * When AI_PROVIDER=OPENAI (backend secret):
 *   - Zero calls to Gemini
 *   - Zero calls to OCR.Space
 *   - Direct: file → OpenAI → structured invoice JSON
 *
 * OCR.Space is kept available as a future fallback but is NOT called
 * in the normal flow when AI_PROVIDER=OPENAI.
 */

import { supabase } from '../../lib/supabase.js';

const BUCKET = 'facturas-compras';

/**
 * Normalize the raw Edge Function response into the standard format
 * that ImportFacturaWizard already consumes (same as normalizeAIResult in AIService).
 *
 * @param {object} data - Raw data from procesar-factura-compra Edge Function
 * @returns {object} Normalized invoice object
 */
function normalizeEdgeFunctionResult(data) {
  const prov = data?.proveedor || {};
  const fact = data?.factura || {};

  return {
    success: true,
    provider: 'openai-invoice',
    cabecera: {
      proveedor: prov?.nombre || null,
      cif: prov?.nif_cif || null,
      direccion: prov?.direccion || null,
      telefono: prov?.telefono || null,
      email: prov?.email || null,
      numero_factura: fact?.numero_factura || null,
      fecha: fact?.fecha || null,
      fecha_vencimiento: fact?.fecha_vencimiento || null,
      forma_pago: fact?.forma_pago || null,
      moneda: fact?.moneda || 'EUR',
      base_imponible: parseFloat(fact?.base_imponible) || 0,
      iva: parseFloat(fact?.iva) || 0,
      recargo_equivalencia: parseFloat(fact?.recargo_equivalencia) || 0,
      retenciones: parseFloat(fact?.retenciones) || 0,
      total: parseFloat(fact?.total) || 0,
      descuentos: 0,
      portes: 0,
      observaciones: null,
    },
    lineas: (data?.lineas || [])?.map(l => ({
      descripcion_original: l?.descripcion || '',
      referencia_proveedor: l?.codigo || null,
      ean: l?.ean || null,
      cantidad: parseFloat(l?.cantidad) || 0,
      unidad: l?.unidad || null,
      // Logistic split fields
      cantidad_compra: l?.cantidad_compra != null ? parseFloat(l?.cantidad_compra) : null,
      unidad_compra: l?.unidad_compra || null,
      cantidad_inventario: l?.cantidad_inventario != null ? parseFloat(l?.cantidad_inventario) : null,
      unidad_inventario: l?.unidad_inventario || null,
      factor_conversion: l?.factor_conversion != null ? parseFloat(l?.factor_conversion) : null,
      warning_conversion: l?.warning_conversion || null,
      precio_unitario: parseFloat(l?.precio_unitario) || 0,
      precio_bruto: parseFloat(l?.precio_bruto || l?.precio_unitario) || 0,
      descuento: parseFloat(l?.descuento_importe) || 0,
      descuento_porcentaje: parseFloat(l?.descuento_porcentaje) || 0,
      porcentaje_iva: parseFloat(l?.iva_porcentaje) || 10,
      subtotal: parseFloat(l?.base_linea || l?.total_linea) || 0,
      lote: l?.lote || null,
      caducidad: l?.caducidad || null,
      confianza_ia: parseFloat(l?.confianza) || 0,
    })),
    confianza_global: parseFloat(data?.confidence) || 0,
    warnings: data?.warnings || [],
    campos_no_legibles: data?.campos_no_legibles || [],
  };
}

/**
 * Main entry point — process an invoice file.
 *
 * Uploads the file to Supabase Storage, then calls the Edge Function
 * procesar-factura-compra which uses OpenAI (or the configured provider)
 * to extract structured invoice data.
 *
 * @param {File} file - PDF or image file
 * @param {Function} [onProgress] - Optional progress callback: onProgress(step, message)
 * @returns {Promise<{success: boolean, result?: object, storagePath?: string, error?: string}>}
 */
export async function processInvoice(file, onProgress) {
  const notify = (step, msg) => {
    if (typeof onProgress === 'function') onProgress(step, msg);
  };

  let storagePath = null;

  try {
    // ── Step 1: Validate session ──────────────────────────────────────────
    const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
    const session = sessionData?.session;
    if (sessionError || !session) {
      return { success: false, error: 'La sesión ha caducado. Inicia sesión de nuevo.' };
    }

    // ── Step 2: Upload file to Supabase Storage ───────────────────────────
    notify('upload', 'Subiendo documento...');
    const ext = file?.name?.split('.')?.pop() || 'jpg';
    storagePath = `facturas/${Date.now()}_${Math.random()?.toString(36)?.slice(2)}.${ext}`;

    const { error: uploadError } = await supabase?.storage
      ?.from(BUCKET)
      ?.upload(storagePath, file, { contentType: file?.type, upsert: false });

    if (uploadError) {
      return { success: false, error: `Error subiendo archivo: ${uploadError?.message}`, storagePath: null };
    }

    notify('upload', '✓ Documento guardado en almacenamiento');

    // ── Step 3: Call Edge Function (OpenAI processes directly) ────────────
    // No OCR.Space. No intermediate text extraction.
    // File → OpenAI → Structured JSON
    notify('ai', 'Procesando factura con IA (OpenAI)...');

    const { data: edgeData, error: edgeError } = await supabase?.functions?.invoke(
      'procesar-factura-compra',
      {
        body: {
          filePath: storagePath,
          bucket: BUCKET,
          mimeType: file?.type,
          fileName: file?.name,
        },
      }
    );

    if (edgeError) {
      return {
        success: false,
        error: `Error en el procesamiento IA: ${edgeError?.message}`,
        storagePath,
      };
    }

    if (!edgeData?.success) {
      return {
        success: false,
        error: edgeData?.error || 'No se ha podido procesar el documento',
        storagePath,
      };
    }

    notify('ai', `✓ Factura procesada (confianza: ${edgeData?.data?.confidence || 0}%)`);

    // ── Step 4: Normalize to standard format ─────────────────────────────
    notify('normalize', 'Normalizando datos...');
    const normalized = normalizeEdgeFunctionResult(edgeData?.data);

    notify('normalize', `✓ Normalización completada — ${normalized?.lineas?.length || 0} líneas detectadas`);

    return {
      success: true,
      result: { ...normalized, storagePath },
      storagePath,
      provider: edgeData?.provider,
      model: edgeData?.model,
    };

  } catch (err) {
    console.error('[ocrInvoiceService] Error:', err?.message);
    return {
      success: false,
      error: err?.message || 'Error inesperado al procesar la factura',
      storagePath,
    };
  }
}

/**
 * Build a normalized invoice object from manual entry form data.
 * Produces the SAME structure as processInvoice() so the wizard
 * can continue through the same matching → review → saveFactura() flow.
 *
 * @param {object} manualData - Form data from manual entry
 * @returns {object} Normalized invoice object (same format as processInvoice result)
 */
export function buildManualInvoiceResult(manualData) {
  const lineas = (manualData?.lineas || [])?.map((l, idx) => ({
    descripcion_original: l?.descripcion || '',
    referencia_proveedor: l?.referencia_proveedor || null,
    ean: l?.ean || null,
    cantidad: parseFloat(l?.cantidad) || 0,
    unidad: l?.unidad || null,
    precio_unitario: parseFloat(l?.precio_unitario) || 0,
    precio_bruto: parseFloat(l?.precio_unitario) || 0,
    descuento: parseFloat(l?.descuento) || 0,
    descuento_porcentaje: parseFloat(l?.descuento_porcentaje) || 0,
    porcentaje_iva: parseFloat(l?.porcentaje_iva) || 10,
    subtotal: (parseFloat(l?.cantidad) || 0) * (parseFloat(l?.precio_unitario) || 0),
    lote: l?.lote || null,
    caducidad: l?.caducidad || null,
    confianza_ia: 100, // Manual entry = full confidence
  }));

  return {
    success: true,
    provider: 'manual',
    cabecera: {
      proveedor: manualData?.proveedor || null,
      cif: manualData?.cif || null,
      direccion: null,
      telefono: null,
      email: null,
      numero_factura: manualData?.numero_factura || null,
      fecha: manualData?.fecha || null,
      fecha_vencimiento: manualData?.fecha_vencimiento || null,
      forma_pago: manualData?.forma_pago || null,
      moneda: 'EUR',
      base_imponible: parseFloat(manualData?.base_imponible) || 0,
      iva: parseFloat(manualData?.iva) || 0,
      recargo_equivalencia: 0,
      retenciones: 0,
      total: parseFloat(manualData?.total) || 0,
      descuentos: 0,
      portes: 0,
      observaciones: manualData?.observaciones || null,
    },
    lineas,
    confianza_global: 100,
    warnings: [],
    campos_no_legibles: [],
    storagePath: null,
    _isManual: true,
  };
}

export default { processInvoice, buildManualInvoiceResult };
